import React, { useEffect, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% { 
    transform: translate3d(0,0,0); 
  }
  40%, 43% { 
    transform: translate3d(0, -6px, 0); 
  }
  70% { 
    transform: translate3d(0, -3px, 0); 
  }
  90% { 
    transform: translate3d(0, -1px, 0); 
  }
`;

const pulse = keyframes`
  0% { 
    transform: scale(1); 
    opacity: 1; 
  }
  50% { 
    transform: scale(1.02); 
    opacity: 0.9; 
  }
  100% { 
    transform: scale(1); 
    opacity: 1; 
  }
`;

const progressAnimation = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingCard = styled.div`
  max-width: 28rem;
  width: 100%;
`;

const IconContainer = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
`;

const MainIcon = styled.div`
  position: relative;
  display: inline-block;
`;

const IconCircle = styled.div`
  width: 4rem;
  height: 4rem;
  background: linear-gradient(135deg, #6366f1 0%, #9333ea 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulse} 3s infinite ease-in-out;
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.25);
`;

const Icon = styled.span`
  font-size: 1.75rem;
  will-change: transform;
  animation: ${bounce} 3s infinite ease-in-out;
  animation-delay: 0.1s;
`;

const ProgressContainer = styled.div`
  margin-bottom: 1rem;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 0.5rem;
  background-color: #e5e7eb;
  border-radius: 9999px;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(135deg, #6366f1 0%, #9333ea 100%);
  border-radius: 9999px;
  position: relative;
  overflow: hidden;
  animation: ${progressAnimation} 4s linear infinite;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.3);
    animation: ${pulse} 1s infinite;
  }
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.4rem;
`;

const ContentContainer = styled.div`
  text-align: center;
`;

const Title = styled.p`
  font-size: 0.9rem;
  font-weight: normal;
  color: #1f2937;
  transition: all 0.5s ease-in-out;
`;

const LoadingScreen = () => {
  const loadingSteps = [
    {
      icon: '🧠',
      text: 'Thinking this through for you...',
    },
    {
      icon: '🔍',
      text: 'Checking what I know that might help...',
    },
    {
      icon: '✨',
      text: 'Got it — almost ready!',
    },
  ];

  const [currentStep, setCurrentStep] = useState(0);
  const [displayProgress, setDisplayProgress] = useState(0);

  // Memoized step rotation function
  const rotateStep = useCallback(() => {
    setCurrentStep((prev) => (prev + 1) % loadingSteps.length);
    setDisplayProgress(0);
  }, [loadingSteps.length]);

  useEffect(() => {
    // Single interval for step rotation (no rapid progress updates)
    const stepInterval = setInterval(rotateStep, 4000);

    // Slower progress display update (less frequent re-renders)
    const progressInterval = setInterval(() => {
      setDisplayProgress((prev) => {
        if (prev >= 100) return 0;
        return prev + 2.5; // Larger increments, fewer updates
      });
    }, 100); // Less frequent updates

    return () => {
      clearInterval(stepInterval);
      clearInterval(progressInterval);
    };
  }, [rotateStep]);

  return (
    <Container>
      <LoadingCard>
        <IconContainer>
          <MainIcon>
            <IconCircle>
              <Icon>{loadingSteps[currentStep].icon}</Icon>
            </IconCircle>
          </MainIcon>
        </IconContainer>

        <ProgressContainer>
          <ProgressBar>
            <ProgressFill />
          </ProgressBar>
          <ProgressText>{Math.round(displayProgress)}% complete</ProgressText>
        </ProgressContainer>

        <ContentContainer>
          <Title>{loadingSteps[currentStep].text}</Title>
        </ContentContainer>
      </LoadingCard>
    </Container>
  );
};

export default LoadingScreen;
